import React, { ReactNode } from 'react';
import DynamicTable from '@atlaskit/dynamic-table';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { css } from '@emotion/core';

import { textOverflowHidden } from '@/styles';
import { Ticket } from '@/components/primitive';
import { formatDateForDisplay } from '@/utils';
import { SloIncidents } from '@/ui';

import { fields, compactFields } from './constants';

type TableSize = 'compact' | 'full';

const renderCell = (incident: API.HOT, key: keyof API.HOT, size: TableSize) => {
  let cellContent: ReactNode;

  switch (key) {
    case 'owner':
      cellContent = incident.owner;
      break;
    case 'issueCreated':
      cellContent = (
        <div css={textOverflowHidden}>{formatDateForDisplay(incident.issueCreated)}</div>
      );
      break;
    case 'issueKey':
      cellContent = (
        <Tooltip content={incident.issueKey} position="left">
          <Ticket ticket={incident.issueKey} href={incident.url} />
        </Tooltip>
      );
      break;
    case 'severityLevel':
      cellContent =
        size === 'compact' ? incident.severityLevel.slice(0, 1) : incident.severityLevel;
      break;
    case 'status':
      cellContent = <Lozenge>{incident.status}</Lozenge>;
      break;
    case 'summary':
      cellContent =
        size === 'compact' ? (
          <Tooltip content={incident.summary} position="left">
            <span
              css={[
                css`
                  width: 150px;
                  display: block;
                  border: none;
                `,
                textOverflowHidden,
              ]}
            >
              {incident.summary}
            </span>
          </Tooltip>
        ) : (
          <Tooltip content={incident.summary} position="left">
            <div css={textOverflowHidden}>{incident.summary}</div>
          </Tooltip>
        );
      break;
    default:
      cellContent = incident[key];
  }

  return cellContent;
};

interface IncidentsProps {
  data: SloIncidents;
  isLoading: boolean;
  rowsPerPage?: number;
  size?: TableSize;
  caption?: React.ReactNode;
}

const Incidents: React.FC<IncidentsProps> = ({
  data,
  rowsPerPage = Infinity,
  isLoading,
  size = 'full',
  caption,
}) => (
  <DynamicTable
    testId="incidents"
    head={{ cells: size === 'compact' ? compactFields : fields }}
    rows={data.map(row => ({
      cells: (size === 'compact' ? compactFields : fields).map(field => ({
        content: renderCell(row, field.key, size),
        key: row[field.key],
      })),
    }))}
    rowsPerPage={rowsPerPage}
    isLoading={isLoading}
    isFixedSize
    caption={caption}
  />
);

export default Incidents;
